import * as React from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import {
  VARIABLE_CATEGORY_STYLES,
  getVariableCategory,
  getVariableDisplayLabel
} from '../HTMLEditorQuill/VariableBlot';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export interface VariableTextInputHandle {
  insertVariable: (variable: string) => void;
  focus: () => void;
}

function createChip(variable: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.setAttribute('data-variable', variable);
  span.setAttribute('contenteditable', 'false');
  const category = getVariableCategory(variable);
  span.className = `vti__chip vti__chip--${category}`;
  const label = document.createElement('span');
  label.className = 'vti__chipLabel';
  label.textContent = getVariableDisplayLabel(variable);
  const remove = document.createElement('span');
  remove.className = 'vti__chipRemove';
  remove.setAttribute('data-variable-remove', 'true');
  remove.setAttribute('role', 'button');
  remove.setAttribute('aria-label', `Remove ${getVariableDisplayLabel(variable)} variable`);
  remove.setAttribute('title', 'Remove variable');
  remove.textContent = '×';
  span.appendChild(label);
  span.appendChild(remove);
  return span;
}

function getPlainText(el: HTMLElement): string {
  let text = '';
  el.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const variable = (node as HTMLElement).getAttribute('data-variable');
      if (variable) {
        text += variable;
      }
    }
  });
  return text;
}

function renderToDOM(el: HTMLElement, value: string): void {
  el.innerHTML = '';
  if (!value) return;
  const parts = value.split(/(\{\{[^}]+\}\})/g);
  parts.forEach(part => {
    if (/^\{\{[^}]+\}\}$/.test(part)) {
      el.appendChild(createChip(part));
    } else if (part) {
      el.appendChild(document.createTextNode(part));
    }
  });
}

function setCursorAfter(node: Node): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function checkAndConvertVariable(
  div: HTMLElement,
  onChange: (v: string) => void,
  lastValueRef: React.MutableRefObject<string>
): void {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;

  const range = sel.getRangeAt(0);
  if (!range.collapsed) return;

  const container = range.endContainer;
  if (container.nodeType !== Node.TEXT_NODE) return;

  const text = container.textContent || '';
  const offset = range.endOffset;
  const before = text.substring(0, offset);

  const match = before.match(/\{\{([^}]*)\}\}$/);
  if (!match) return;

  const variable = match[0];
  const startIndex = before.length - variable.length;
  const afterText = text.substring(offset);

  const parent = container.parentNode!;
  const beforeNode = document.createTextNode(text.substring(0, startIndex));
  const chip = createChip(variable);
  const spaceNode = document.createTextNode('\u200B');
  const afterNode = document.createTextNode(afterText);

  parent.insertBefore(beforeNode, container);
  parent.insertBefore(chip, container);
  parent.insertBefore(spaceNode, container);
  parent.insertBefore(afterNode, container);
  parent.removeChild(container);

  setCursorAfter(spaceNode);

  const newValue = getPlainText(div);
  lastValueRef.current = newValue;
  onChange(newValue);
}

export const VariableTextInput = forwardRef<VariableTextInputHandle, Props>((props, ref) => {
  const { value, onChange, placeholder, disabled, style, className } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef(value);
  const isComposingRef = useRef(false);

  useEffect(() => {
    const div = divRef.current;
    if (!div) return;
    const current = getPlainText(div);
    if (current !== value) {
      renderToDOM(div, value || '');
      lastValueRef.current = value || '';
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    insertVariable: (variable: string) => {
      const div = divRef.current;
      if (!div || disabled) return;
      div.focus();
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) range.deleteContents();
      const chip = createChip(variable);
      const space = document.createTextNode('\u200B');
      range.insertNode(space);
      range.insertNode(chip);
      setCursorAfter(space);
      const newValue = getPlainText(div);
      lastValueRef.current = newValue;
      onChange(newValue);
    },
    focus: () => divRef.current?.focus()
  }));

  const handleInput = useCallback(() => {
    if (isComposingRef.current) return;
    const div = divRef.current;
    if (!div) return;
    const text = getPlainText(div);
    lastValueRef.current = text;
    onChange(text);
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        return;
      }

      if (e.key === 'Backspace') {
        const div = divRef.current;
        if (!div) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        if (!range.collapsed) return; // пусть браузер сам удаляет выделенное

        const { startContainer, startOffset } = range;

        // Случай 1: курсор в текстовом узле
        if (startContainer.nodeType === Node.TEXT_NODE) {
          if (startOffset === 0) {
            // Смотрим что стоит перед этим текстовым узлом
            const prev = startContainer.previousSibling;
            if (prev && (prev as HTMLElement).getAttribute?.('data-variable')) {
              e.preventDefault();
              prev.parentNode?.removeChild(prev);
              const newValue = getPlainText(div);
              lastValueRef.current = newValue;
              onChange(newValue);
              return;
            }
          }
          return; // обычный символ — браузер сам удалит
        }

        // Случай 2: курсор стоит прямо в div (между нодами)
        if (startContainer === div) {
          const nodeBeforeCursor = div.childNodes[startOffset - 1];
          if (nodeBeforeCursor && (nodeBeforeCursor as HTMLElement).getAttribute?.('data-variable')) {
            e.preventDefault();
            div.removeChild(nodeBeforeCursor);
            const newValue = getPlainText(div);
            lastValueRef.current = newValue;
            onChange(newValue);
            return;
          }
        }
      }

      if (e.key === '}') {
        setTimeout(() => {
          const div = divRef.current;
          if (div) checkAndConvertVariable(div, onChange, lastValueRef);
        }, 0);
      }
    },
    [onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const fragment = document.createDocumentFragment();
      text.split(/(\{\{[^}]+\}\})/g).forEach(part => {
        if (/^\{\{[^}]+\}\}$/.test(part)) {
          fragment.appendChild(createChip(part));
        } else if (part) {
          fragment.appendChild(document.createTextNode(part));
        }
      });
      range.insertNode(fragment);
      range.collapse(false);
      const div = divRef.current!;
      const newValue = getPlainText(div);
      lastValueRef.current = newValue;
      onChange(newValue);
    },
    [onChange]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const remove = target.closest('[data-variable-remove]');
      if (!remove) return;
      event.preventDefault();
      event.stopPropagation();
      const chip = remove.closest('[data-variable]') as HTMLElement | null;
      const div = divRef.current;
      if (!chip || !div) return;
      chip.remove();
      const text = getPlainText(div);
      lastValueRef.current = text;
      onChange(text);
      div.focus();
    },
    [onChange]
  );

  return (
    <div
      ref={divRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      className={`vti${disabled ? ' vti--disabled' : ''}${className ? ` ${className}` : ''}`}
      data-placeholder={!value && placeholder ? placeholder : undefined}
      style={style}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onPaste={handlePaste}
      onCompositionStart={() => {
        isComposingRef.current = true;
      }}
      onCompositionEnd={() => {
        isComposingRef.current = false;
        handleInput();
      }}
    />
  );
});

VariableTextInput.displayName = 'VariableTextInput';
